import { Suspense, lazy, useCallback } from 'react';
import {
  Users,
  GraduationCap,
  Briefcase,
  Sparkles,
  Lock,
  ArrowRight,
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LEARN_KHMER_RESOURCES } from '@/lib/constants';
import { useAuth } from '@/lib/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import { Nav } from '@/components/layout/Nav';
import { Footer } from '@/components/layout/Footer';
import { ProfileCard } from '@/components/profile/ProfileCard';
import {
  useMapProfiles,
  useProfileStats,
  useRandomPreview,
} from '@/lib/hooks/useProfiles';
import { usePageContent, useSavePageBlock } from '@/lib/hooks/usePageContent';
import { EditableText } from '@/components/cms/EditableText';

const MemberMapView = lazy(() =>
  import('@/components/map/MemberMapView').then((m) => ({
    default: m.MemberMapView,
  })),
);

type CmsProps = {
  isAdmin: boolean;
  contentMap: Record<string, Record<string, unknown>>;
  onSave: (blockKey: string, content: Record<string, unknown>) => Promise<void>;
};

export function Landing() {
  const { isAuthenticated, loaded, profile, profileLoaded } = useAuth();
  const isAdmin = profile?.verification === 'admin_verified';

  const { data: pageBlocks } = usePageContent('landing');
  const { mutateAsync: saveBlock } = useSavePageBlock('landing');

  const contentMap: Record<string, Record<string, unknown>> = {};
  for (const block of pageBlocks ?? []) {
    contentMap[block.block_key] = block.content;
  }

  const onSave = useCallback(
    async (blockKey: string, content: Record<string, unknown>) => {
      if (!profile?.id) throw new Error('Not signed in — please reload and try again.');
      await saveBlock({ blockKey, content, updatedBy: profile.id });
      toast.success('Content saved');
    },
    [profile?.id, saveBlock],
  );

  // Wait for both auth session AND profile to load before deciding to redirect.
  // Without this, isAdmin is false while profile is still null and non-admins
  // get redirected instantly — but so do admins because their profile hasn't
  // arrived yet.
  if (loaded && isAuthenticated && !profileLoaded) {
    return null;
  }

  if (loaded && isAuthenticated && !isAdmin) {
    return <Navigate to="/feed" replace />;
  }

  if (Capacitor.isNativePlatform()) {
    return <Navigate to="/signin" replace />;
  }

  const cms: CmsProps = { isAdmin, contentMap, onSave };

  return (
    <div id="top" className="min-h-screen bg-cream">
      <Nav />
      <Hero cms={cms} />
      <Story cms={cms} />
      <Pillars cms={cms} />
      <MapSection />
      <DirectorySection cms={cms} />
      <Events cms={cms} />
      <JoinCta cms={cms} />
      <Faq cms={cms} />
      <LearnKhmer />
      <Footer />
    </div>
  );
}

function Eyebrow({
  children,
  variant = 'dark',
}: {
  children: React.ReactNode;
  variant?: 'dark' | 'light';
}) {
  const base = 'inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em]';
  const tone =
    variant === 'light'
      ? 'text-gold-soft'
      : 'text-gold-dark';
  return (
    <span className={`${base} ${tone}`}>
      <span
        className="inline-block h-1.5 w-1.5 rounded-full bg-gold"
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

function E({ blockKey, fallback, cms, className }: {
  blockKey: string;
  fallback: string;
  cms: CmsProps;
  className?: string;
}) {
  if (!cms.isAdmin) {
    const stored = cms.contentMap[blockKey];
    if (stored) {
      // Render stored HTML from TipTap JSON — use dangerouslySetInnerHTML for display
      return (
        <span
          className={className}
          dangerouslySetInnerHTML={{ __html: tiptapJsonToHtml(stored) }}
        />
      );
    }
    return <>{fallback}</>;
  }

  return (
    <EditableText
      blockKey={blockKey}
      initialContent={cms.contentMap[blockKey] ?? null}
      fallbackHtml={fallback}
      onSave={cms.onSave}
      className={className}
    />
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function tiptapJsonToHtml(json: Record<string, unknown>): string {
  const doc = json as { type?: string; content?: unknown[] };
  if (!doc.content) return '';
  return (doc.content as Array<{ type?: string; content?: unknown[]; text?: string }>)
    .map((node) => {
      if (node.type === 'paragraph') {
        const text = (node.content as Array<{ text?: string }> | undefined)
          ?.map((t) => escapeHtml(t.text ?? ''))
          .join('') ?? '';
        return text;
      }
      return '';
    })
    .join(' ');
}

function Hero({ cms }: { cms: CmsProps }) {
  return (
    <header className="relative overflow-hidden bg-gradient-to-br from-navy-darker via-navy-deep to-navy text-cream">
      <div
        className="pointer-events-none absolute -right-40 top-20 h-[540px] w-[540px] rounded-full bg-gold/15 blur-3xl"
        aria-hidden="true"
      />
      <div className="container grid gap-12 py-20 md:grid-cols-[1.2fr_1fr] md:items-center md:py-28">
        <div>
          <Eyebrow variant="light">
            Est. 2013 · Worldwide · <span className="khmer normal-case">ខ្មែរ</span>
          </Eyebrow>
          <h1 className="mt-6 font-display text-5xl leading-[1.05] tracking-tight text-cream text-balance sm:text-6xl md:text-[4.25rem]">
            <E
              blockKey="hero.headline"
              fallback="A community for Cambodian professionals, everywhere."
              cms={cms}
            />
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-cream/80">
            <E
              blockKey="hero.subheading"
              fallback="Since 2013, we've brought Cambodian professionals together to share career experiences, trade opportunities, and support each other. Join us to see the full member map, directory, and monthly digest."
              cms={cms}
            />
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/signup">
                Join the network <ArrowRight className="ml-1" />
              </Link>
            </Button>
            <Button asChild variant="ghost-gold" size="lg">
              <Link to="/signin">Sign in</Link>
            </Button>
          </div>

          <HeroStats />
        </div>

        <div className="relative mx-auto hidden md:block">
          <div
            className="absolute inset-0 rounded-full bg-gold/25 blur-3xl"
            aria-hidden="true"
          />
          <img
            src="/brand/cpn_leafseal.svg"
            alt="CPN leaf seal — Est. 2013"
            className="relative h-[420px] w-[420px] drop-shadow-[0_20px_60px_rgba(212,162,76,0.35)]"
          />
        </div>
      </div>
    </header>
  );
}

function Story({ cms }: { cms: CmsProps }) {
  return (
    <section id="story" className="relative border-y border-border/70 bg-cream-warm/60">
      <div className="container py-20">
        <div className="grid gap-12 md:grid-cols-[0.8fr_1.2fr] md:items-center">
          <div className="fade-in mx-auto max-w-xs">
            <img
              src="/brand/cpn_leafseal.svg"
              alt=""
              aria-hidden="true"
              className="w-full"
            />
          </div>
          <div className="fade-in">
            <Eyebrow>Our story · since 2013</Eyebrow>
            <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
              <E
                blockKey="story.headline"
                fallback="Thirteen years of Cambodian professionals sharing what they know."
                cms={cms}
              />
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink/80">
              <E
                blockKey="story.body1"
                fallback="Founded in 2013, The Cambodian Professionals Network was created to bring young and young-at-heart professionals together to share career experiences and provide insight into the different fields we respectively work in — inside a safe and welcoming community."
                cms={cms}
              />
            </p>
            <p className="mt-4 text-base leading-relaxed text-ink/80">
              <E
                blockKey="story.body2"
                fallback="Our goals are simple: share current opportunities — jobs, referrals, collaborations — talk about career paths and the roads people took to get there, and host events to gain perspective and give support to anyone strengthening their career or changing direction."
                cms={cms}
              />
            </p>
            <p className="mt-4 text-sm leading-relaxed text-muted">
              <E
                blockKey="story.bodhi"
                fallback="The bodhi leaf in our seal is the tree under which the Buddha attained enlightenment. ~95% of Cambodians are Theravada Buddhist, so there is no more fitting symbol for a Khmer community built on sharing wisdom."
                cms={cms}
              />
            </p>

            <blockquote className="mt-8 border-l-2 border-gold bg-cream px-6 py-5 font-display text-lg italic text-navy">
              <E
                blockKey="story.quote"
                fallback='"A safe, welcoming room for Khmer professionals to share what they know and open doors for each other."'
                cms={cms}
              />
              <cite className="mt-2 block font-sans text-xs not-italic uppercase tracking-widest text-muted">
                — The CPN mission, since 2013
              </cite>
            </blockquote>
          </div>
        </div>
      </div>
    </section>
  );
}

const PILLARS = [
  {
    icon: Users,
    blockKey: 'pillars.networking',
    title: 'Networking',
    body: 'Find Khmer professionals in your city, industry, or field. Skip the cold intro — we already share something.',
  },
  {
    icon: GraduationCap,
    blockKey: 'pillars.career',
    title: 'Career & Mentorship',
    body: 'Pair with mentors, share job openings, help younger professionals navigate the paths we figured out alone.',
  },
  {
    icon: Briefcase,
    blockKey: 'pillars.business',
    title: 'Business & Ventures',
    body: 'Support Cambodian-owned businesses, find co-founders and investors, build companies that serve our community.',
  },
  {
    icon: Sparkles,
    blockKey: 'pillars.culture',
    title: 'Culture & Heritage',
    body: 'Celebrate Khmer culture, give back to Cambodia, make sure the next generation knows where they come from.',
  },
];

function Pillars({ cms }: { cms: CmsProps }) {
  return (
    <section id="pillars" className="bg-cream py-20">
      <div className="container">
        <div className="fade-in mx-auto max-w-2xl text-center">
          <Eyebrow>What we do</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
            <E
              blockKey="pillars.headline"
              fallback="Four ways we show up for each other."
              cms={cms}
            />
          </h2>
          <p className="mt-4 text-base text-muted">
            <E
              blockKey="pillars.subheading"
              fallback="CPN is more than a group chat. Here's how members connect, grow, and give back."
              cms={cms}
            />
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map(({ icon: Icon, title, body, blockKey }) => (
            <article
              key={title}
              className="fade-in rounded-2xl border border-border/70 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold-dark">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="mt-5 font-display text-xl text-navy">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/75">
                <E blockKey={blockKey} fallback={body} cms={cms} />
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function MapSection() {
  const { isAuthenticated } = useAuth();

  return (
    <section id="map" className="bg-cream-warm/50 py-20">
      <div className="container">
        <div className="fade-in mx-auto max-w-2xl text-center">
          <Eyebrow>Members worldwide</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
            From Phnom Penh to Paris, <em>we are everywhere</em>.
          </h2>
          <p className="mt-4 text-base text-muted">
            248 members mapped across 49 cities and 13 countries. Click any pin
            to see who's there.
          </p>
        </div>

        {!isAuthenticated && (
          <div className="mt-10 flex flex-col items-center justify-between gap-3 rounded-xl border border-border/70 bg-white p-5 shadow-sm sm:flex-row">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy/5 text-navy">
                <Lock className="h-4 w-4" />
              </span>
              <span className="text-sm text-navy/80">
                <strong className="text-navy">Member names are hidden.</strong>{' '}
                Sign in to see who's in each city.
              </span>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="ghost-dark" size="sm">
                <Link to="/signin">Sign in</Link>
              </Button>
              <Button asChild variant="gold" size="sm">
                <Link to="/signup">Join →</Link>
              </Button>
            </div>
          </div>
        )}

        <MapPreview />
      </div>
    </section>
  );
}

function DirectorySection({ cms }: { cms: CmsProps }) {
  return (
    <section id="directory" className="bg-cream py-20">
      <div className="container">
        <div className="fade-in mx-auto max-w-2xl text-center">
          <Eyebrow>Member directory</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
            <E
              blockKey="directory.headline"
              fallback="Search and find your people."
              cms={cms}
            />
          </h2>
          <p className="mt-4 text-base text-muted">
            <E
              blockKey="directory.subheading"
              fallback="Filter by industry, country, or keyword. Full profiles visible to signed-in members."
              cms={cms}
            />
          </p>
        </div>

        <DirectoryPreview />
      </div>
    </section>
  );
}

const EVENTS = [
  {
    tag: 'Virtual · Upcoming',
    day: 'TBD',
    blockKey: 'events.intro_night',
    title: 'Member Intro Night',
    meta: '🌐 Zoom · Global',
    body: 'New members take 90 seconds to introduce themselves live. Existing members share one thing they need and one thing they can offer.',
  },
  {
    tag: 'Regional · Quarterly',
    day: 'TBD',
    blockKey: 'events.city_meetup',
    title: 'City Chapter Meetup',
    meta: '📍 LA · Phnom Penh · London',
    body: 'Informal happy hours and dinners in each city. Hosted by local members, open to anyone passing through.',
  },
  {
    tag: 'Monthly',
    day: '1st',
    blockKey: 'events.career_panel',
    title: 'Career Path Panel',
    meta: '🎤 3 members · 1 industry',
    body: 'Three senior members share their career paths in a single industry.',
  },
];

function Events({ cms }: { cms: CmsProps }) {
  return (
    <section id="events" className="bg-cream-warm/60 py-20">
      <div className="container">
        <div className="fade-in mx-auto max-w-2xl text-center">
          <Eyebrow>Community events</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
            <E
              blockKey="events.headline"
              fallback="Where we gather, in person and online."
              cms={cms}
            />
          </h2>
          <p className="mt-4 text-base text-muted">
            <E
              blockKey="events.subheading"
              fallback="From career panels to cultural gatherings. Events organized by members."
              cms={cms}
            />
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {EVENTS.map((e) => (
            <article
              key={e.title}
              className="fade-in flex gap-5 rounded-2xl border border-border/70 bg-white p-6 shadow-sm"
            >
              <div className="flex w-24 shrink-0 flex-col items-center justify-center rounded-xl bg-navy text-cream">
                <span className="px-2 text-center text-[10px] uppercase tracking-widest text-gold-soft">
                  {e.tag}
                </span>
                <span className="mt-1 font-display text-2xl text-gold-bright">
                  {e.day}
                </span>
              </div>
              <div>
                <h3 className="font-display text-lg text-navy">{e.title}</h3>
                <p className="mt-1 text-xs uppercase tracking-widest text-muted">
                  {e.meta}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink/75">
                  <E blockKey={e.blockKey} fallback={e.body} cms={cms} />
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function JoinCta({ cms }: { cms: CmsProps }) {
  return (
    <section id="join" className="relative overflow-hidden bg-navy py-20 text-cream">
      <div
        className="pointer-events-none absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-gold/15 blur-3xl"
        aria-hidden="true"
      />
      <div className="container text-center">
        <Eyebrow variant="light">Join the network</Eyebrow>
        <h2 className="mt-4 font-display text-4xl leading-tight text-cream text-balance sm:text-5xl">
          <E
            blockKey="joincta.headline"
            fallback="Ready to meet your people, worldwide?"
            cms={cms}
          />
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-cream/80">
          <E
            blockKey="joincta.body"
            fallback="Signup takes 60 seconds. You'll get the monthly digest, full access to the member map and directory, and an invite to city meetups in your area."
            cms={cms}
          />
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild variant="gold" size="lg">
            <Link to="/signup">
              Create your CPN account <ArrowRight className="ml-1" />
            </Link>
          </Button>
          <Button asChild variant="ghost-gold" size="lg">
            <Link to="/signin">Already a member? Sign in</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

const FAQS = [
  {
    blockKey: 'faq.who_can_join',
    q: 'Who can join CPN?',
    a: "Any professional who identifies as Cambodian — by heritage, upbringing, or chosen community. We're inclusive of the full diaspora, mixed-heritage folks, and professionals working in Cambodia. Students and early-career welcome. \"Young and young at heart.\"",
  },
  {
    blockKey: 'faq.fee',
    q: 'Is there a membership fee?',
    a: 'No. CPN is free and volunteer-run. If we ever add paid programs (mentorship cohorts, conferences), the core network stays free and accessible.',
  },
  {
    blockKey: 'faq.sign_in',
    q: 'Why do I need to sign in to see names?',
    a: 'Member privacy. Our directory shows real names, roles, and links to personal intros — so we only share them with verified members. Signup is free and takes under a minute.',
  },
  {
    blockKey: 'faq.privacy',
    q: 'Will my information be shared outside CPN?',
    a: "Your email goes only to the CPN newsletter. Your directory profile is visible only to other signed-in members. We don't sell or share data with third parties.",
  },
  {
    blockKey: 'faq.bodhi',
    q: 'What does the bodhi leaf mean?',
    a: "The bodhi tree (Ficus religiosa) is where the Buddha attained enlightenment. ~95% of Cambodians are Theravada Buddhist, making it the most culturally resonant symbol for a Khmer professional community. Look closely — the leaf's left edge curves like a C, and the right edge like a P. CPN is baked into the silhouette.",
  },
];

function Faq({ cms }: { cms: CmsProps }) {
  return (
    <section id="faq" className="bg-cream py-20">
      <div className="container max-w-3xl">
        <div className="fade-in text-center">
          <Eyebrow>Frequently asked</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
            Questions <em>new members</em> often ask.
          </h2>
        </div>

        <div className="mt-10 space-y-3">
          {FAQS.map((item) => (
            <details
              key={item.q}
              className="group rounded-xl border border-border/70 bg-white px-5 py-4 shadow-sm open:shadow-md"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-base text-navy">
                {item.q}
                <span
                  className="text-gold-dark transition-transform group-open:rotate-45"
                  aria-hidden="true"
                >
                  +
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-ink/75">
                <E blockKey={item.blockKey} fallback={item.a} cms={cms} />
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function LearnKhmer() {
  return (
    <section id="learn-khmer" className="bg-cream-warm/60 py-20">
      <div className="container">
        <div className="fade-in mx-auto max-w-2xl text-center">
          <Eyebrow>
            <a href="https://www.reankhmer.org" target="_blank" rel="noopener noreferrer" className="hover:underline underline-offset-2">
              ReanKhmer.org
            </a>
          </Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy text-balance sm:text-5xl">
            Learn Khmer Through <em>Cultural Immersion</em>
          </h2>
          <p className="mt-4 text-base text-muted">
            Connect language to belonging and identity through authentic Cambodian experiences.
            Free interactive tools, apps, games, guides, word lookups, community pages,
            and app comparisons — all in one place.{' '}
            <a
              href="https://www.reankhmer.org"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-navy underline-offset-2 hover:underline"
            >
              Visit ReanKhmer.org for details.
            </a>
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {LEARN_KHMER_RESOURCES.map((r) => (
            <a
              key={r.href}
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 rounded-xl border border-border/70 bg-white p-5 text-center shadow-sm transition-shadow hover:shadow-md"
            >
              <span className="text-3xl" aria-hidden="true">{r.emoji}</span>
              <span className="font-display text-sm font-semibold text-navy transition-colors group-hover:text-gold-dark">
                {r.title}
              </span>
              <span className="text-xs leading-snug text-muted">{r.desc}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroStats() {
  const { data, isLoading } = useProfileStats();

  const items = [
    { n: data?.members, fallback: '—', l: 'Members mapped' },
    { n: data?.cities, fallback: '—', l: 'Cities worldwide' },
    { n: data?.countries, fallback: '—', l: 'Countries' },
  ];

  return (
    <dl className="mt-12 grid max-w-lg grid-cols-3 gap-8 border-t border-cream/10 pt-8">
      {items.map((s) => (
        <div key={s.l}>
          <dt className="font-display text-3xl text-gold-bright">
            {isLoading ? (
              <span className="inline-block h-9 w-16 animate-pulse rounded-md bg-cream/10" />
            ) : (
              (s.n ?? s.fallback)
            )}
          </dt>
          <dd className="mt-1 text-xs uppercase tracking-widest text-cream/60">
            {s.l}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function MapPreview() {
  const { data, isLoading, isError } = useMapProfiles();
  const { isAuthenticated } = useAuth();
  const profiles = data ?? [];

  return (
    <div className="relative mt-6 overflow-hidden rounded-2xl border border-border/70 bg-white shadow-sm">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-cream/0 to-transparent" />
      <div className="absolute left-4 top-4 z-[500] rounded-xl border border-border/70 bg-white/95 px-3 py-2 text-xs font-medium text-navy shadow-sm backdrop-blur">
        {isLoading ? 'Loading…' : `${profiles.length} members mapped`}
      </div>
      <div className="absolute right-4 top-4 z-[500]">
        <Button asChild variant="gold" size="sm">
          <Link to="/map">
            Open full map
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
      <div className="h-[420px]">
        {isError ? (
          <div className="flex h-full items-center justify-center text-sm text-destructive">
            Couldn't load the map.
          </div>
        ) : isLoading ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : (
          <Suspense fallback={<Skeleton className="h-full w-full rounded-none" />}>
            <MemberMapView profiles={profiles} interactive={false} showNames={isAuthenticated} />
          </Suspense>
        )}
      </div>
    </div>
  );
}

function DirectoryPreview() {
  const { isAuthenticated } = useAuth();
  const { data, isLoading, isError } = useRandomPreview(12);

  if (isError) {
    return (
      <p className="mt-10 text-center text-sm text-destructive">
        Couldn't load the directory preview.
      </p>
    );
  }

  return (
    <>
      <div className="relative mt-10">
        <div
          className={`grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${
            !isAuthenticated ? 'pointer-events-none select-none blur-sm' : ''
          }`}
          aria-hidden={!isAuthenticated}
        >
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-44 rounded-2xl" />
              ))
            : (data ?? []).map((p) => <ProfileCard key={p.id} profile={p} />)}
        </div>

        {!isAuthenticated && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-2xl">
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-white/95 px-8 py-6 text-center shadow-md backdrop-blur-sm">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy/5 text-navy">
                <Lock className="h-5 w-5" />
              </span>
              <p className="font-display text-lg text-navy">
                Members visible to signed-in members only.
              </p>
              <p className="max-w-xs text-sm text-muted">
                Join free to see full profiles, connect with members, and browse the directory.
              </p>
              <div className="flex gap-2">
                <Button asChild variant="ghost-dark" size="sm">
                  <Link to="/signin">Sign in</Link>
                </Button>
                <Button asChild variant="gold" size="sm">
                  <Link to="/signup">Join free →</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-center">
        <Button asChild variant="ghost-dark" size="lg">
          <Link to="/directory">
            Browse all members
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </>
  );
}
